import { useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Logo } from "../../components/common/Logo";
import { authService } from "../../lib/api/auth";
import { useTranslation } from "react-i18next";

type PageState = 'form' | 'loading' | 'success' | 'error' | 'no-token';

export const ResetPasswordPage = () => {
    const { t } = useTranslation("common");
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [pageState, setPageState] = useState<PageState>(token ? 'form' : 'no-token');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Prevent double submission
    const hasSubmitted = useRef(false);

    const validatePassword = (pwd: string): string | null => {
        if (pwd.length < 8) return t("auth.passwordMinLength", "Password must be at least 8 characters long.");
        if (!/\d/.test(pwd)) return t("auth.passwordRequireNumber", "Password must contain at least one number.");
        if (!/[a-zA-Z]/.test(pwd)) return t("auth.passwordRequireLetter", "Password must contain at least one letter.");
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token || hasSubmitted.current) return;

        // Validation
        if (password !== confirmPassword) {
            setError(t("auth.passwordsDoNotMatch", "Passwords do not match."));
            return;
        }

        const validationError = validatePassword(password);
        if (validationError) {
            setError(validationError);
            return;
        }

        hasSubmitted.current = true;
        setIsLoading(true);
        setError(null);

        try {
            await authService.resetPassword({ token, newPassword: password });
            setPageState('success');
        } catch (err: any) {
            hasSubmitted.current = false;
            const message = err.response?.data?.error || err.message || t("auth.resetPasswordError");
            setError(message);

            // If token is invalid/expired, show error state
            if (message.toLowerCase().includes('expired') || message.toLowerCase().includes('invalid')) {
                setPageState('error');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoToLogin = () => {
        navigate('/?modal=login');
    };

    return (
        <div className="min-h-screen bg-background-page flex flex-col">
            {/* Navigation */}
            <nav className="container-custom py-4 px-4 sm:py-6 sm:px-6">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <Logo className="w-6 h-6" />
                        <span className="text-lg sm:text-2xl font-medium font-display text-text-primary tracking-tight">
                            {t("brandName")}
                        </span>
                    </Link>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {/* Form State */}
                    {pageState === 'form' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 border border-neutral-200 dark:border-neutral-800">
                            {/* Lock Icon */}
                            <div className="flex justify-center mb-8 h-8">
                                <h1 className="relative text-2xl font-semibold text-text-primary dark:text-neutral-100">
                                    <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap h-full">
                                        <Logo className="w-7 h-7" />
                                        <span className="w-4 h-4 text-neutral-300 dark:text-neutral-600" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_de24d260.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_de24d260.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                    </div>
                                    {t("auth.resetPasswordTitle", "Reset Password")}
                                </h1>
                            </div>

                            <p className="text-neutral-600 dark:text-neutral-400 mb-6 text-center">
                                {t("auth.resetPasswordDescription", "Enter your new secure password below.")}
                            </p>

                            {/* Error Message */}
                            {error && (
                                <div className="bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-800 rounded-lg p-3 mb-4">
                                    <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-1">
                                        {t("auth.newPassword", "New Password")}
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        required
                                        className="input w-full"
                                        placeholder={t("auth.passwordPlaceholder")}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                                        {t("auth.passwordRequirements", "Min 8 characters, letters & numbers required")}
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-1">
                                        {t("auth.confirmPassword", "Confirm Password")}
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        required
                                        className="input w-full"
                                        placeholder={t("auth.passwordPlaceholder")}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="btn-primary w-full py-3"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center">
                                            <div className="spinner mr-2" />
                                            {t("actions.loading", "Loading...")}
                                        </span>
                                    ) : (
                                        t("auth.resetPassword", "Reset Password")
                                    )}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Success State */}
                    {pageState === 'success' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                            <div className="w-20 h-20 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="w-10 h-10 text-success-600 dark:text-success-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_893eceab.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_893eceab.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                            </div>
                            <h1 className="text-2xl font-semibold text-text-primary mb-2">
                                {t("auth.passwordResetSuccess", "Password Reset Successful!")} 🎉
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                                {t("auth.passwordResetSuccessMessage", "Your password has been updated successfully. You can now sign in with your new password.")}
                            </p>
                            <button
                                onClick={handleGoToLogin}
                                className="btn-primary w-full py-3"
                            >
                                {t("auth.signIn", "Sign In")}
                            </button>
                        </div>
                    )}

                    {/* Error State */}
                    {pageState === 'error' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                            <div className="w-20 h-20 bg-error-100 dark:bg-error-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="w-10 h-10 text-error-600 dark:text-error-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_6fc8aad2.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_6fc8aad2.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                            </div>
                            <h1 className="text-2xl font-semibold text-text-primary mb-2">
                                {t("auth.resetFailed", "Reset Failed")}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                                {error || t("auth.couldNotResetPassword", "We couldn't reset your password.")}
                            </p>
                            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 mb-6">
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                    {t("auth.thisCouldHappenIf", "This could happen if:")}
                                </p>
                                <ul className="text-sm text-neutral-500 dark:text-neutral-500 mt-2 space-y-1">
                                    <li>• {t("auth.linkExpired", "The reset link has expired")}</li>
                                    <li>• {t("auth.linkAlreadyUsed", "The link was already used")}</li>
                                    <li>• {t("auth.linkInvalid", "The link is invalid")}</li>
                                </ul>
                            </div>
                            <Link
                                to="/forgot-password"
                                className="btn-primary w-full py-3 inline-block text-center"
                            >
                                {t("auth.requestNewLink", "Request New Reset Link")}
                            </Link>
                        </div>
                    )}

                    {/* No Token State */}
                    {pageState === 'no-token' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                            <div className="w-20 h-20 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="w-10 h-10 text-warning-600 dark:text-warning-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_85f8f3e1.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_85f8f3e1.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                            </div>
                            <h1 className="text-2xl font-semibold text-text-primary mb-2">
                                {t("auth.missingResetToken", "Missing Reset Token")}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                                {t("auth.noTokenMessage", "No reset token was found in the URL. Please click the link in your email again, or request a new password reset.")}
                            </p>
                            <Link
                                to="/forgot-password"
                                className="btn-primary w-full py-3 inline-block text-center"
                            >
                                {t("auth.requestPasswordReset", "Request Password Reset")}
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-neutral-200 dark:border-neutral-800 py-6">
                <div className="container-custom text-center">
                    <p className="text-sm text-neutral-500 dark:text-neutral-500">
                        {t("auth.copyright", { year: new Date().getFullYear() })}
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default ResetPasswordPage;
